import React, { useCallback, useEffect, useRef, useState } from 'react';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Draggable from 'react-draggable';
import { useLocation } from 'react-router-dom';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { getBuildConfig, tracker } from 'app-components/utils/core';

import { useAppSelector } from 'hooks/redux';

import GuideReferenceMap from './GuideReferenceMap';
import getPendoAPI from './PendoAPI';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';

type OpenMethod = 'topbar_button' | 'hash_link' | 'custom_event' | 'keyboard';

const UserGuide = () => {
  const PendoAPI = getPendoAPI();
  const [guideUrl, setGuideUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const dragRef = React.useRef<HTMLDivElement>({} as HTMLDivElement);
  const location = useLocation();

  const trackingPropsRef = useRef<{ guideId: string; guideType: string; openMethod: OpenMethod }>({
    guideId: '',
    guideType: '',
    openMethod: 'topbar_button',
  });
  const hasDraggedRef = useRef(false);

  const isUserGuideEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'userGuideEnabled'));

  const setFullPathGuideUrl = (relativeUrl: string) => {
    const normalizedUrl = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
    setGuideUrl(`${getBuildConfig().user_guide_url}${normalizedUrl}`);
  };

  const trackOpen = useCallback(
    (guideId: string, guideType: string, openMethod: OpenMethod) => {
      trackingPropsRef.current = { guideId, guideType, openMethod };
      hasDraggedRef.current = false;
      tracker.timeEvent(tracker.events.userGuideClose);
      tracker.track(tracker.events.userGuideOpen, {
        guideId,
        guideType,
        openMethod,
        pageContext: location.pathname,
      });
    },
    [location.pathname],
  );

  const trackClose = useCallback(() => {
    const { guideId, guideType } = trackingPropsRef.current;
    tracker.track(tracker.events.userGuideClose, {
      guideId,
      guideType,
      pageContext: location.pathname,
    });
  }, [location.pathname]);

  const handleDragStart = useCallback(() => {
    if (!hasDraggedRef.current) {
      hasDraggedRef.current = true;
      const { guideId, guideType } = trackingPropsRef.current;
      tracker.track(tracker.events.userGuideDrag, {
        guideId,
        guideType,
        pageContext: location.pathname,
      });
    }
  }, [location.pathname]);

  // Event listener for user interactions that may trigger a guide
  useEffect(() => {
    if (!isUserGuideEnabled) return;

    const handleEvent = (event: MouseEvent | KeyboardEvent) => {
      let target = event.target as HTMLElement;
      let openMethod: OpenMethod = 'topbar_button';

      // Handle keyboard navigation (Enter/Space)
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== ' ') return; // exit if not Enter or Space
        target = document.activeElement as HTMLElement; // use the focused element
        openMethod = 'keyboard';
      }

      // Look for hash-based guide trigger: #openUserGuide=<path>
      const anchor = target.tagName === 'A' ? target : target.closest('a');
      if (anchor instanceof HTMLAnchorElement && anchor.hash.startsWith('#openUserGuide=')) {
        event.preventDefault();

        const relativeGuideUrl = decodeURIComponent(anchor.hash.replace('#openUserGuide=', ''));
        if (relativeGuideUrl) {
          setFullPathGuideUrl(relativeGuideUrl);
          setOpen(true);
          trackOpen(relativeGuideUrl, 'USER_GUIDE', 'hash_link');
        }

        // Dismiss any current Pendo guide. This could happen when the user clicks on a User Guide link
        // within a Pendo guide.
        window.pendo?.onGuideDismissed?.();
        return;
      }

      // Handle ID-based Pendo/UserGuide trigger
      //
      const elementWithId = target.id ? target : target.closest('[id]');
      if (!elementWithId?.id) return;
      if (!elementWithId.id.startsWith('guide-')) return; // Early exit if not a guide ID

      const guideReference = GuideReferenceMap[elementWithId.id];
      if (!guideReference) return;

      if (guideReference.guideType === 'PENDO') {
        getPendoAPI()?.showGuideById(guideReference.guideId);
      } else if (guideReference.guideType === 'USER_GUIDE') {
        // need to add base URL
        const relativeGuideUrl = guideReference.guideId;
        setFullPathGuideUrl(relativeGuideUrl);
        setOpen(true);
        trackOpen(elementWithId.id, guideReference.guideType, openMethod);
      }
    };

    // Use window.dispatchEvent(new CustomEvent("user-guide:open", { detail: { "guideUrl": <relative-url> }})). This can
    // be used from within Pendo guides (e.g. resource center) to open the User Guide.
    //
    const handleCustomEvent = (event: CustomEvent) => {
      const { guideUrl: relativeGuideUrl } = event.detail;
      if (relativeGuideUrl) {
        setFullPathGuideUrl(relativeGuideUrl);
        setOpen(true);
        trackOpen(relativeGuideUrl, 'USER_GUIDE', 'custom_event');

        // Dismiss the Pendo guide
        window.setTimeout(() => {
          // if pendo guide is up, dismiss it
          if (window.pendo) {
            window.pendo.onGuideDismissed();
          }
        }, 0);
      }
    };

    window.addEventListener('click', handleEvent);
    window.addEventListener('keydown', handleEvent);
    window.addEventListener('user-guide:open', handleCustomEvent as EventListener);

    return () => {
      if (isUserGuideEnabled) {
        window.removeEventListener('click', handleEvent);
        window.removeEventListener('keydown', handleEvent);
        window.removeEventListener('user-guide:open', handleCustomEvent as EventListener);
      }
    };
  }, [isUserGuideEnabled, PendoAPI, trackOpen]);

  const handleClose = () => {
    trackClose();
    setOpen(false);
  };

  const renderContent = () => {
    return (
      <Stack flex={1} sx={{ height: '100%', width: '100%' }}>
        <StdDialogTitle showCloseButton onClose={handleClose}>
          Quicken University
        </StdDialogTitle>
        <iframe
          src={guideUrl ?? getBuildConfig().user_guide_url}
          style={{ height: '100%', width: '100%', border: 'none' }}
          title='Embedded Quicken University Page'
        />
      </Stack>
    );
  };

  return !open || !isUserGuideEnabled ? null : (
    <Draggable nodeRef={dragRef} handle='#draggable-dialog-title' onStart={handleDragStart}>
      <Paper
        ref={(ref) => {
          dragRef.current = ref ?? ({} as HTMLDivElement);
        }}
        elevation={24}
        sx={{
          position: 'fixed',
          bottom: '25px',
          right: '25px',
          width: '33%',
          minWidth: '500px',
          height: '75vh',
          zIndex: 2000,
          overflow: 'hidden',
        }}
      >
        {renderContent()}
      </Paper>
    </Draggable>
  );
};

export default UserGuide;
